import React, { memo, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { noop } from 'utils'
import useSubjects from 'hooks/useSubjects'
import { findNotebooksByAgoraSubjects } from 'api/material'
import {
  resolveAgoraSubjects,
  getAllAgoraMappings,
  getAgoraSubjectInfo,
  getCsvLevelLabel
} from 'utils/agoraSubjectResolver'
import Button from 'components/buttons/Button'
import Select from '../Select'
import styles from './NotebookSelect.module.css'

const customStyles = {
  select: styles.customSelect
}

const dedup = (ids = []) => [...new Set(ids.filter(Boolean))].sort()

function NotebookSelect({
  selectedNotebookSubjectId,
  selectedNotebookUrl,
  selectedNotebookTitle,
  subjectLevel,
  subjectSublevel,
  onSelect = noop
}) {
  const { subjects, isFetching: isFetchingSubjects } = useSubjects()
  const [showAllLevels, setShowAllLevels] = useState(false)
  const [resource, setResource] = useState({
    subjectId: selectedNotebookSubjectId,
    url: selectedNotebookUrl,
    title: selectedNotebookTitle || ''
  })
  const handleLoadPDF = () => {
    if (resource.url)
      return onSelect(resource.subjectId, resource.url, resource.title)
  }

  const agoraSubjectIds = useMemo(() => {
    if (showAllLevels)
      return dedup(getAllAgoraMappings().map(m => m.agoraSubjectId))
    if (!resource.subjectId) return []
    const { matches } = resolveAgoraSubjects(
      resource.subjectId,
      subjectLevel,
      subjectSublevel
    )
    return dedup(matches.map(m => m.agoraSubjectId))
  }, [resource.subjectId, subjectLevel, subjectSublevel, showAllLevels])

  const hasNoMapping =
    !showAllLevels && !!resource.subjectId && agoraSubjectIds.length === 0

  const { data: notebooks = [], isFetching: isFetchingNotebooks } = useQuery({
    queryKey: ['notebooks', agoraSubjectIds],
    queryFn: () => findNotebooksByAgoraSubjects(agoraSubjectIds),
    enabled: agoraSubjectIds.length > 0
  })

  const notebookOptions = useMemo(() => {
    const sorted = [...notebooks].sort((a, b) =>
      (a.nombre || '').localeCompare(b.nombre || '')
    )
    if (!showAllLevels) return sorted
    return sorted.map(n => {
      const info = getAgoraSubjectInfo(n._agoraSubjectId)
      const prefix = info
        ? `${info.name} · ${getCsvLevelLabel(info.level)} · `
        : ''
      return { ...n, _displayName: prefix + (n.nombre || '') }
    })
  }, [notebooks, showAllLevels])

  return (
    <div className={styles.notebookSelect}>
      {!showAllLevels && (
        <Select
          label='Asignatura'
          placeholder='Selecciona asignatura'
          isLoading={isFetchingSubjects}
          options={subjects.sort((a, b) => a.label.localeCompare(b.label))}
          value={resource.subjectId}
          onChange={e =>
            setResource({ subjectId: e.target.value, url: '', title: '' })
          }
          customStyles={customStyles}
        />
      )}
      <Select
        label='Cuadernillo'
        placeholder={
          hasNoMapping
            ? 'Sin material disponible para esta asignatura'
            : 'Selecciona cuadernillo'
        }
        isLoading={isFetchingNotebooks}
        options={notebookOptions}
        labelKey={showAllLevels ? '_displayName' : 'nombre'}
        value={notebookOptions.find(n => n.url === resource.url)?.id || null}
        onChange={e => {
          const notebookId = e.target.value
          const notebook = notebookOptions.find(n => n.id === notebookId)
          if (!notebook) return
          const { url, nombre } = notebook
          setResource({ ...resource, url, title: nombre })
        }}
        customStyles={customStyles}
        disabled={(!showAllLevels && !resource.subjectId) || hasNoMapping}
      />
      <button
        type='button'
        className={styles.panicToggle}
        onClick={() => {
          setShowAllLevels(v => !v)
          setResource(r => ({ ...r, url: '', title: '' }))
        }}
      >
        {showAllLevels ? 'Filtrar por asignatura' : 'Ver todo el material'}
      </button>
      <div className={styles.button}>
        <Button
          label='Cargar cuadernillo'
          size='small'
          type='accent-primary'
          disabled={!resource.url}
          onClick={handleLoadPDF}
        />
      </div>
    </div>
  )
}

export default memo(NotebookSelect)
